/**
 * 모바일 앱(TWA Android, Capacitor iOS/Android) 환경 감지 유틸
 *
 * 결제 페이지 등 앱스토어 정책상 노출하면 안 되는 화면을 조건부로 숨기기 위해 사용.
 * 한 번 감지되면 localStorage에 저장하여 SPA 라우팅 시에도 일관되게 동작.
 *
 * 감지 우선순위:
 *  1) window.Capacitor 객체 존재 (Capacitor 7+ 자동 주입)
 *  2) document.referrer.startsWith('android-app://') (TWA Android)
 *  3) URL 쿼리 파라미터 ?source=app 또는 ?utm_source=twa (강제 플래그)
 *  4) User-Agent에 'wv'(Android WebView) + standalone display-mode (PWA 강제 설치)
 *  5) localStorage에 저장된 이전 감지 결과
 */

const STORAGE_KEY = 'whatquiz_in_app_env';

/** 한 번 감지된 환경을 영구 저장 (TWA는 매번 referrer 안 줄 수 있어서 캐싱 필요) */
function rememberEnv(envInfo) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(envInfo));
  } catch (e) {
    // localStorage 비활성 시 무시
  }
}

function loadRememberedEnv() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

/**
 * 현재 앱 환경 정보 객체 반환
 * @returns {{ isApp: boolean, platform: 'ios' | 'android' | 'web', source: string }}
 */
export function getAppEnv() {
  if (typeof window === 'undefined') {
    return { isApp: false, platform: 'web', source: 'ssr' };
  }

  if (window.Capacitor && typeof window.Capacitor.getPlatform === 'function') {
    const platform = window.Capacitor.getPlatform();
    if (platform === 'ios' || platform === 'android') {
      const env = { isApp: true, platform, source: 'capacitor' };
      rememberEnv(env);
      return env;
    }
  }

  if (typeof document !== 'undefined' && document.referrer && document.referrer.startsWith('android-app://')) {
    const env = { isApp: true, platform: 'android', source: 'twa' };
    rememberEnv(env);
    return env;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('source') || params.get('utm_source');
    if (source === 'app' || source === 'twa' || source === 'capacitor-ios' || source === 'capacitor-android') {
      const platform = source.includes('ios') ? 'ios' : 'android';
      const env = { isApp: true, platform, source };
      rememberEnv(env);
      return env;
    }
  } catch (e) {
    // URL 파싱 실패 시 무시
  }

  const remembered = loadRememberedEnv();
  if (remembered && remembered.isApp) {
    return remembered;
  }

  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const isAndroidWebView = /\bwv\b/.test(ua) && /Android/i.test(ua);
  const isIosStandalone = typeof navigator !== 'undefined' && navigator.standalone === true;
  const isStandaloneDisplay =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(display-mode: standalone)').matches;

  if (isAndroidWebView && isStandaloneDisplay) {
    const env = { isApp: true, platform: 'android', source: 'webview-standalone' };
    rememberEnv(env);
    return env;
  }

  if (isIosStandalone) {
    const env = { isApp: true, platform: 'ios', source: 'ios-standalone' };
    rememberEnv(env);
    return env;
  }

  return { isApp: false, platform: 'web', source: 'browser' };
}

/** 모바일 앱(TWA / Capacitor)에서 동작 중인지 단순 boolean 반환 */
export function isInApp() {
  return getAppEnv().isApp;
}

/** iOS 앱 환경에서 동작 중인지 (Capacitor iOS) */
export function isInIosApp() {
  const env = getAppEnv();
  return env.isApp && env.platform === 'ios';
}

/** Android 앱 환경에서 동작 중인지 (TWA / Capacitor Android) */
export function isInAndroidApp() {
  const env = getAppEnv();
  return env.isApp && env.platform === 'android';
}

/**
 * Capacitor 네이티브 앱(iOS/Android Capacitor)에서 동작 중인지 명시적 확인.
 *
 * `isInApp()` 보다 더 엄격하게 "실제 Capacitor 네이티브 빌드"인 경우에만 true.
 * - ✅ iOS Capacitor (WKWebView)
 * - ✅ Android Capacitor (WebView)
 * - ❌ Android TWA (Chrome 셸) — false 반환 (Chrome OAuth 사용 가능)
 * - ❌ 일반 웹 / PWA standalone — false
 *
 * 네이티브 SDK(Google Sign-In 등) 호출 분기 시 사용 권장.
 * (TWA에서 잘못 native 분기 진입하는 회귀 방지)
 */
export function isCapacitorNative() {
  if (typeof window === 'undefined') return false;
  if (!window.Capacitor) return false;
  if (typeof window.Capacitor.isNativePlatform !== 'function') return false;
  return window.Capacitor.isNativePlatform();
}

/**
 * 앱 환경에서 외부 브라우저(Chrome / Safari)로 URL 열기.
 * - Capacitor: window.Capacitor.Plugins.Browser.open() 또는 fallback으로 window.open
 * - TWA: window.open(url, '_blank')은 Chrome Custom Tabs로 열림
 * - 일반 웹: window.open(url, '_blank')
 */
export function openInExternalBrowser(url) {
  if (typeof window === 'undefined') return;
  try {
    if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.Browser) {
      window.Capacitor.Plugins.Browser.open({ url });
      return;
    }
  } catch (e) {
    // Capacitor 플러그인 미설치 등 - 아래 fallback으로
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}
